import type { MenuDish } from '../../../models';
import type { MenuStoreTemplateMenuRow } from '../../../models/menu-store.entity';

/**
 * 解析模板行上的「其他标识」：接口可能为 `string[]` 或 `*` 拼接字符串。
 */
function parseOtherDetailKeys(v: unknown): string[] | undefined {
    if (Array.isArray(v)) {
        const arr = v.filter((x): x is string => typeof x === 'string' && x.length > 0);
        return arr.length ? arr : undefined;
    }
    if (typeof v === 'string' && v.trim()) {
        const arr = v.split('*').filter(Boolean);
        return arr.length ? arr : undefined;
    }
    return undefined;
}

/**
 * 将菜单商店模板 `structure` 中的菜品行转为 `MenuDish`，供与菜单库共用的 `MenuLibraryAddDishDialogContent` 展示。
 * 模板字段少于门店菜品时未出现的域保持 undefined，表单中对应为默认占位。
 * 商店接口常在行上返回 `dish_image` / `dish_image_small`（无 `image_url` / `icon_url` 时须映射到 `bigFoodImage` / `foodImage` 方能在只读弹窗展示图）。
 */
export function menuStoreTemplateRowToMenuDish(row: MenuStoreTemplateMenuRow): MenuDish {
    /** 模板行上可能存在的扩展字段（与落库菜品对齐） */
    const extra = row as MenuStoreTemplateMenuRow & Record<string, unknown>;
    /** 接口常见主图：仅字符串 URL 有效（偶见空对象 `{}`，不可 String() 成 [object Object]） */
    const dishImageMain = typeof row.dish_image === 'string' ? row.dish_image.trim() : '';
    /** 小图 URL：同上，非字符串视为无图 */
    const dishImageSmall = typeof row.dish_image_small === 'string' ? row.dish_image_small.trim() : '';

    const print_class = typeof extra.print_class === 'number' ? extra.print_class : undefined;
    const item_type = typeof extra.item_type === 'number' ? extra.item_type : undefined;
    const special_attributes = typeof extra.special_attributes === 'number' ? extra.special_attributes : undefined;
    const minimum_time = typeof extra.minimum_time === 'number' ? extra.minimum_time : undefined;
    const rules_ignore_the_display =
        typeof extra.rules_ignore_the_display === 'number' ? extra.rules_ignore_the_display : undefined;
    const rule_lnr_limit_enable =
        typeof extra.rule_lnr_limit_enable === 'number' ? extra.rule_lnr_limit_enable : undefined;
    const rule_table_max_num = typeof extra.rule_table_max_num === 'number' ? extra.rule_table_max_num : undefined;
    const rule_lnr_limit_price = typeof extra.rule_lnr_limit_price === 'number' ? extra.rule_lnr_limit_price : undefined;

    const item_i18n_raw = extra.item_i18n;
    const item_i18n = typeof item_i18n_raw === 'string' ? item_i18n_raw : undefined;

    const allergenArr = Array.isArray(row.allergen)
        ? row.allergen.filter((x): x is string => typeof x === 'string' && x.length > 0)
        : undefined;

    return {
        item_name1: row.item_name1,
        item_name2: row.item_name2,
        item_description: row.item_description ?? null,
        nlu: row.nlu,
        class_id: row.class_id,
        tax_group: row.tax_group != null ? Number(row.tax_group) : undefined,
        major_group: row.major_group,
        family_group: row.family_group,
        price_1: row.price_1,
        takeout_price_1: row.takeout_price_1,
        unit_1: row.unit_1 ?? null,
        price_2: row.price_2,
        price_3: row.price_3,
        price_4: row.price_4,
        price_5: row.price_5,
        unit_2: row.unit_2 ?? null,
        unit_3: row.unit_3 ?? null,
        unit_4: row.unit_4 ?? null,
        unit_5: row.unit_5 ?? null,
        rule_lnr_max_num: row.rule_lnr_max_num,
        ext_dish_info: row.ext_dish_info,
        allergen: allergenArr?.length ? allergenArr : undefined,
        other_detail_info: parseOtherDetailKeys(row.other_detail_info),
        required_condiment: row.required_condiment ?? undefined,
        item_i18n,
        print_class,
        item_type,
        special_attributes,
        minimum_time,
        rules_ignore_the_display,
        rule_lnr_limit_enable,
        rule_table_max_num,
        rule_lnr_limit_price,
        /** 只读详情缩略区：`MenuLibraryAddDishDialogContent` 读 `bigFoodImage`/`foodImage`，须映射商店接口的 `dish_image` / `dish_image_small` */
        bigFoodImage:
            (typeof row.image_url === 'string' ? row.image_url.trim() : '') ||
            dishImageMain ||
            dishImageSmall ||
            undefined,
        foodImage:
            (typeof row.icon_url === 'string' ? row.icon_url.trim() : '') ||
            dishImageSmall ||
            dishImageMain ||
            undefined,
        /** 非字符串（如空对象 `{}`）不透传，避免下游 `String({})` 成 `[object Object]` */
        dish_image: typeof row.dish_image === 'string' ? row.dish_image : null,
    };
}
